import { useEffect } from "react"

import { Divider, Flex, Form, Input, Select } from "antd"
import { useDispatch, useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"

import { getCountries, updateBusinessDetails } from "../redux/businessDetails.thunks"

const customizeRequiredMark = (label, { required }) => (
  <>
    {label}
    {required && <span className="required-mark">*</span>}
  </>
)

const BusinessDetailsForm = ({ onSubmit, setIsSubmitting, setIsValidForm }) => {
  const [searchParams] = useSearchParams()
  const userId = searchParams.get("user_id")
  const dispatch = useDispatch()
  const { isAdmin } = useSelector((state) => state.auth)
  const [form] = Form.useForm()
  const country = Form.useWatch("country", form)

  const { countries, businessDetails } = useSelector((state) => state.businessDetails)

  const unitedStatesId = countries?.find((c) => c.name === "United States")?.id
  const countrySelected = countries?.find((c) => c.id === country)
  const isUnitedStateSelected = countrySelected && countrySelected.id === unitedStatesId

  const values = Form.useWatch([], form)

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setIsValidForm(true))
      .catch((err) => {
        if (err?.outOfDate) return
        setIsValidForm(false)
      })
  }, [form, values])

  const handleSubmit = (values) => {
    setIsSubmitting(true)
    const clearedValues = {}
    for (const [key, value] of Object.entries(values)) {
      clearedValues[key] = value || null
    }
    dispatch(
      updateBusinessDetails({
        body: {
          ...clearedValues
        },
        params: {
          ...(isAdmin && { user_id: userId })
        }
      })
    )
      .unwrap()
      .then(() => {
        onSubmit(values)
        form.resetFields()
        setIsSubmitting(false)
      })
      .catch((error) => {
        console.log(error)
        setIsSubmitting(false)
      })
  }

  useEffect(() => {
    dispatch(getCountries())
  }, [])

  useEffect(() => {
    form.setFieldValue(
      "country",
      countries.find((c) => c.name === businessDetails?.country)?.id ?? null
    )
  }, [countries])

  return (
    <Form
      id="business-details-form"
      layout="vertical"
      onFinish={handleSubmit}
      form={form}
      requiredMark={customizeRequiredMark}
      initialValues={{
        ...businessDetails,
        country: countries.find((c) => c.name === businessDetails?.country)?.id ?? null
      }}
    >
      <Form.Item
        rules={[{ required: true, message: "This field is required" }]}
        label="Business name"
        name="name"
        required
      >
        <Input placeholder="e.g, Tesla Inc" />
      </Form.Item>
      <Divider />
      <Form.Item
        rules={[{ required: true, message: "This field is required" }]}
        label="Address line 1"
        name="address_line_1"
        required
      >
        <Input placeholder="e.g, 1 Tesla Road" />
      </Form.Item>
      <Form.Item label="Address line 2" name="address_line_2">
        <Input placeholder="e.g, Floor 2 (optional)" />
      </Form.Item>
      <Flex gap={16}>
        <Form.Item
          rules={[{ required: true, message: "This field is required" }]}
          label="City/town"
          name="city"
          required
        >
          <Input placeholder="e.g, Austin" />
        </Form.Item>
        <Form.Item
          rules={[{ required: true, message: "This field is required" }]}
          label="Postal code"
          name="postal_code"
          required
        >
          <Input placeholder="e.g, 78725" />
        </Form.Item>
      </Flex>
      <Form.Item
        rules={[{ required: true, message: "This field is required" }]}
        label="Country/region"
        name="country"
        required
      >
        <Select
          showSearch
          className="country-state-select"
          placeholder="e.g, United States"
          optionFilterProp="children"
        >
          {countries.map((country) => (
            <Select.Option key={country.id} value={country.id}>
              {country.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      {isUnitedStateSelected && (
        <Form.Item name="state" preserve={false}>
          <Select showSearch className="country-state-select" placeholder="e.g, Alabama">
            {countrySelected.state.map((state) => (
              <Select.Option key={state} value={state}>
                {state}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}
      <Divider />
      <Form.Item
        label={
          isUnitedStateSelected ? "Business registration number" : "Tax registration number (TRN)"
        }
        name="registration_number"
      >
        <Input
          placeholder={isUnitedStateSelected ? "MT12345678 (optional)" : "12-3456789 (optional)"}
        />
      </Form.Item>
    </Form>
  )
}

export default BusinessDetailsForm
